import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
} from '@mui/material';
import {
  ShoppingCart as ShoppingCartIcon,
} from '@mui/icons-material';
import { formatCurrency } from '../utils/currency';
import { quantiteACombler } from '../utils/reappro';
import { ajouterAuPanier } from '../utils/panier';
import { useStock } from '../contexts/StockContext';
import { productService } from '../services/productService';
// ⚠️ `purchaseOrderService` a quitté ce fichier : il ne crée plus de commandes,
// il remplit des paniers. Un import sans usage est du code qu'on croit encore
// appelé — même famille que le wrapper `getAllPayments` retiré au lot RU4-b.
import { useLanguage } from '../contexts/LanguageContext';
import { cle, useMessageTraduit } from '../utils/messageTraduit';

const StockOrderSelector = ({ open, onClose, onOrdersCreated }) => {
  const { t } = useLanguage();
  const { currentStock } = useStock();
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState({});
  const [quantities, setQuantities] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useMessageTraduit();
  const [success, setSuccess] = useMessageTraduit();

  useEffect(() => {
    if (open) {
      loadProducts();
    }
  }, [open]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const response = await productService.getProducts({ 
        low_stock_only: true,
        per_page: 1000 
      });
      setProducts(response.products || []);
    } catch (error) {
      console.error('Erreur lors du chargement des produits:', error);
      setError(cle('stockOrderSelector.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleProductSelect = (productId) => {
    setSelectedProducts(prev => ({
      ...prev,
      [productId]: !prev[productId]
    }));
    
    // ⚠️ ON N'AMORCE PLUS LA QUANTITÉ À LA SÉLECTION. Elle l'était à 1, ce qui
    // ÉCRASAIT le calcul de `quantiteProposee` dès la première case cochée —
    // le champ affichait alors 1 sur un produit qu'il fallait combler de 20.
    // `quantities` ne porte plus que ce que l'utilisateur a TAPÉ ; le reste se
    // calcule. *Un état pré-rempli avec une valeur par défaut masque le calcul
    // qui aurait dû la produire.*
  };

  // 🔴 LA QUANTITÉ PAR DÉFAUT COMBLE LE SEUIL, elle ne vaut plus 1. Ce chemin
  // et le bouton « Produits en alerte » du formulaire commandent les MÊMES
  // produits ; ils proposaient deux quantités différentes, et la plus petite —
  // 1 — laissait le produit en alerte après réception. La formule vit désormais
  // dans `utils/reappro.js`, lue par les trois sites.
  // 🔴 UNE SEULE SOURCE POUR CE QUI S'AFFICHE ET CE QUI PART. Le champ montrait
  // `|| 1` et l'envoi lisait la même expression à part : deux écritures de la
  // même valeur, donc deux occasions de diverger — et la divergence aurait été
  // INVISIBLE, l'écran affichant une quantité et le serveur en recevant une
  // autre. C'est le défaut de la charge utile construite par étalement, en plus
  // discret : ici les deux nombres se ressemblent.
  const quantiteProposee = (productId) =>
    quantities[productId]
    || quantiteACombler(products.find((p) => p.id === productId))
    || 1;

  const handleQuantityChange = (productId, quantity) => {
    setQuantities(prev => ({
      ...prev,
      [productId]: Math.max(1, parseInt(quantity) || 1)
    }));
  };

  // 🔴 CE CHEMIN REMPLIT LES PANIERS, IL NE CRÉE PLUS DE COMMANDES. Il en créait
  // directement, une par fournisseur — pendant que le chariot d'une ligne
  // produit, lui, alimentait le panier. **Deux chemins qui remplissent deux
  // choses différentes avec la même icône** : c'est la divergence de trop, et
  // elle serait née le jour où le panier est arrivé.
  // ⚠️ Le groupement par fournisseur ne disparaît pas — il est simplement fait
  // par `ajouterAuPanier`, qui range chaque produit dans le panier de SON
  // fournisseur. La règle est la même, écrite une seule fois.
  const handleCreateOrders = () => {
    const selection = products.filter((p) => selectedProducts[p.id]);
    if (selection.length === 0) {
      setError(cle('stockOrderSelector.selectOneError'));
      return;
    }
    selection.forEach((produit) => ajouterAuPanier(currentStock?.id, {
      ...produit, current_stock: produit.current_stock, min_stock: produit.min_stock,
    }, quantiteProposee(produit.id)));
    setSuccess(cle('cart.addedCount', { count: selection.length }));
    setSelectedProducts({});
    setQuantities({});
    if (onOrdersCreated) onOrdersCreated([]);
    setTimeout(() => { onClose(); setSuccess(null); }, 1500);
  };

  const getSelectedCount = () => {
    return Object.keys(selectedProducts).filter(id => selectedProducts[id]).length;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">{t('stockOrderSelector.title')}</Typography>
          <Chip 
            label={t('stockOrderSelector.selectedCount', { count: getSelectedCount() })}
            color="primary"
            size="small"
          />
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          {t('stockOrderSelector.description')}
        </Typography>

        <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={
                      Object.keys(selectedProducts).some(id => selectedProducts[id]) &&
                      !Object.keys(selectedProducts).every(id => selectedProducts[id])
                    }
                    checked={Object.keys(selectedProducts).every(id => selectedProducts[id])}
                    onChange={(e) => {
                      const newSelection = {};
                      products.forEach(product => {
                        newSelection[product.id] = e.target.checked;
                      });
                      setSelectedProducts(newSelection);
                    }}
                  />
                </TableCell>
                <TableCell>{t('common.product')}</TableCell>
                <TableCell>{t('common.barcode')}</TableCell>
                <TableCell>{t('common.currentStock')}</TableCell>
                <TableCell>{t('common.minStock')}</TableCell>
                <TableCell>{t('common.price')}</TableCell>
                <TableCell>{t('common.quantity')}</TableCell>
                <TableCell>{t('common.supplier')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {products.map((product) => (
                <TableRow key={product.id}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedProducts[product.id] || false}
                      onChange={() => handleProductSelect(product.id)}
                    />
                  </TableCell>
                  <TableCell>{product.name}</TableCell>
                  <TableCell>{product.barcode}</TableCell>
                  <TableCell>
                    <Chip 
                      label={product.current_stock}
                      color={product.is_low_stock ? 'error' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{product.min_stock}</TableCell>
                  <TableCell>{product.price != null ? formatCurrency(product.price, product.currency || 'EUR') : '-'}</TableCell>
                  <TableCell>
                    <TextField
                      type="number"
                      value={quantiteProposee(product.id)}
                      onChange={(e) => handleQuantityChange(product.id, e.target.value)}
                      disabled={!selectedProducts[product.id]}
                      size="small"
                      sx={{ width: 80 }}
                      inputProps={{ min: 1 }}
                    />
                  </TableCell>
                  <TableCell>{product.supplier_name || t('common.none')}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {getSelectedCount() > 0 && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              {t('stockOrderSelector.summaryTitle')}
            </Typography>
            <Typography variant="body2">
              {t('stockOrderSelector.summaryCount', { count: getSelectedCount() })}
            </Typography>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleCreateOrders}
          variant="contained"
          startIcon={<ShoppingCartIcon />}
          disabled={loading || getSelectedCount() === 0}
        >
          {loading ? t('stockOrderSelector.creating') : t('stockOrderSelector.createOrders')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StockOrderSelector; 